import { conditionSchema, MAX_CONDITIONS } from "@amtana/prospect-contract";
import type { ProspectScope } from "@amtana/prospect-contract";
import { z } from "zod";

// A filter ("list" in the URL, as in the reference app) is a saved, rebuildable
// query: up to 10 source analyses and up to 10 AND-ed conditions over prospect
// fields. It is built in the background and then holds a prospect count.

export const listStatusSchema = z.enum(["building", "ready", "empty", "error"]);
export type ListStatus = z.infer<typeof listStatusSchema>;

/** The most prospects one list holds by hand. */
export const MAX_LIST_PICKS = 1000;
export const MAX_PICK_NOTE_CHARS = 500;

/**
 * A prospect put on a list by hand, with a note (ADR-023 §3: an agent adds the prospects it
 * found, and why). A list that holds picks is a picked list: its prospects are its picks, not
 * its rules (D603).
 */
export const listPickSchema = z.object({
  prospectId: z.string(),
  note: z.string(),
  addedAt: z.iso.datetime(),
  /** The agent that added it, or null for a person. */
  addedByAgentId: z.string().nullable(),
});
export type ListPick = z.infer<typeof listPickSchema>;

export const filterListSchema = z.object({
  id: z.string(),
  projectId: z.string(),
  name: z.string(),
  description: z.string(),
  sourceAnalysisIds: z.array(z.string()).max(10),
  conditions: z.array(conditionSchema).max(MAX_CONDITIONS),
  status: listStatusSchema,
  prospectCount: z.number().int().nonnegative(),
  emailCount: z.number().int().nonnegative(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  lastBuiltAt: z.iso.datetime().nullable(),
  /** Empty for a list made by its rules; its prospects for a picked list (D603). */
  picks: z.array(listPickSchema).max(MAX_LIST_PICKS),
});
export type FilterList = z.infer<typeof filterListSchema>;

/** The prospects a list holds: its picks when it has any (a picked list, D603), else its rules. */
export function listScopeOf(
  list: Pick<FilterList, "sourceAnalysisIds" | "conditions" | "picks">,
): ProspectScope {
  if (list.picks.length > 0) return { prospectIds: list.picks.map((pick) => pick.prospectId) };
  return { analysisIds: [...list.sourceAnalysisIds], conditions: [...list.conditions] };
}

export const filterListsSchema = z.object({
  lists: z.array(filterListSchema),
  /** The plan's filter limit; null when unlimited. */
  max: z.number().int().nonnegative().nullable(),
});
export type FilterLists = z.infer<typeof filterListsSchema>;

export const listDefinitionSchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(80),
  description: z.string().trim().max(500).default(""),
  sourceAnalysisIds: z
    .array(z.string())
    .min(1, "Select at least one analysis")
    .max(10, "You can select up to 10 analyses"),
  conditions: z.array(conditionSchema).max(MAX_CONDITIONS),
});
export type ListDefinition = z.input<typeof listDefinitionSchema>;

export const emptyInputSchema = z.object({});
export const listIdInputSchema = z.object({ listId: z.string() });
export const updateListInputSchema = listDefinitionSchema.extend({ listId: z.string() });
export type UpdateListInput = z.input<typeof updateListInputSchema>;
export const listIdsInputSchema = z.object({ listIds: z.array(z.string()).min(1) });
export const moveListsInputSchema = z.object({ listIds: z.array(z.string()).min(1), projectId: z.string() });
export const countSchema = z.object({ changed: z.number().int().nonnegative() });
export type Count = z.infer<typeof countSchema>;

/** Prospects to put on the list named `list`, made as a picked list when it does not exist yet. */
export const addListProspectsInputSchema = z.object({
  list: z.string().trim().min(1).max(80),
  prospects: z
    .array(
      z.object({
        prospectId: z.string().min(1).max(64),
        note: z.string().trim().max(MAX_PICK_NOTE_CHARS).default(""),
      }),
    )
    .min(1)
    .max(100),
});
export type AddListProspectsInput = z.input<typeof addListProspectsInputSchema>;

export const addedListProspectsSchema = z.object({
  list: filterListSchema,
  /** New on the list. */
  added: z.number().int().nonnegative(),
  /** Already there; the note was replaced when one was given. */
  updated: z.number().int().nonnegative(),
});
export type AddedListProspects = z.infer<typeof addedListProspectsSchema>;
